import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Package,
  Boxes,
  Layers,
  Shield,
  Headphones,
  Clock,
  BadgeCheck,
  Ruler,
  Scale,
  FileText,
  Handshake,
  Navigation,
  Warehouse,
  MapPin,
} from "lucide-react";
import { InnerPage } from "@/components/InnerPage";
import { SectionHeader } from "@/components/SectionHeader";
import { mergeHead, pageHead } from "@/lib/seo";
import { breadcrumbsFor, jsonLdScript, serviceJsonLd } from "@/lib/schema";
import { SITE } from "@/lib/site";

const PATH = "/dry-van-freight";

const META_DESCRIPTION =
  "Dry goods and general freight hauling coast to coast with 24/7 dispatch. Full truckload dry van from a New Jersey carrier since 2012 — Fox Trucking.";

const SERVICE_DESCRIPTION =
  "Full truckload dry van and general freight trucking for dry goods and packaged products, hauled coast to coast by Fox Trucking LLC from New Jersey.";

export const Route = createFileRoute("/dry-van-freight")({
  head: () =>
    mergeHead(
      pageHead({
        title: "Dry Van & General Freight Carrier | Fox Trucking NJ",
        description: META_DESCRIPTION,
        path: PATH,
        ogTitle: "Dry Van & General Freight Carrier | Fox Trucking NJ",
        ogDescription: META_DESCRIPTION,
      }),
      {
        scripts: [
          jsonLdScript(
            serviceJsonLd({
              name: "Dry Van & General Freight",
              description: SERVICE_DESCRIPTION,
              path: PATH,
              serviceType: "Full truckload dry van trucking",
              idSuffix: "service",
            }),
          ),
        ],
      },
    ),
  component: DryVanFreightPage,
});

const HAUL_CARDS = [
  {
    i: Package,
    t: "Dry goods",
    d: "Shelf-stable freight that needs a clean trailer and a schedule that holds — from warehouse to warehouse without drama.",
  },
  {
    i: Boxes,
    t: "Packaged products",
    d: "Consumer packaged goods, paper products, and carton freight that ships dry and arrives ready for the floor.",
  },
  {
    i: Layers,
    // TODO: Confirm additional dry van commodity categories Fox Trucking regularly hauls.
    t: "Other general freight",
    d: "[OTHER COMMODITIES - placeholder]. Share the commodity and weight — we’ll confirm equipment and lane fit.",
  },
] as const;

const WHY_SHIPPERS = [
  {
    i: Shield,
    t: "Reliability you can plan around",
    d: "Appointments matter. We treat pickup and delivery windows as commitments, not suggestions.",
  },
  {
    i: Headphones,
    t: "Communication that doesn’t ghost",
    d: "Status updates before you ask. A live dispatcher when plans change mid-route.",
  },
  {
    i: BadgeCheck,
    t: `Experience since ${SITE.foundingYear}`,
    d: `A New Jersey carrier that’s been moving freight for more than a decade — long enough to know what “on time” actually takes.`,
  },
  {
    i: Clock,
    t: "24/7 dispatch",
    d: "Nights, weekends, holidays — someone answers when your load needs a decision.",
  },
] as const;

const PROCESS_STEPS = [
  {
    i: FileText,
    n: "01",
    t: "Send the load brief",
    d: "Origin, destination, commodity, weight, and preferred pickup date. We come back with a straightforward FTL rate.",
  },
  {
    i: Handshake,
    n: "02",
    t: "Lock equipment & appointments",
    d: "Dry van assigned, docks confirmed, and a driver lined up — so your warehouse isn’t waiting on a mystery truck.",
  },
  {
    i: Navigation,
    n: "03",
    t: "Track the haul",
    d: "Your freight moves coast to coast with dispatch available around the clock if weather or traffic shifts the plan.",
  },
  {
    i: Warehouse,
    n: "04",
    t: "Deliver & close out",
    d: "On-time arrival at the consignee, paperwork wrapped, and a clean handoff you don’t have to chase.",
  },
] as const;

const DRY_VAN_FAQS = [
  {
    question: "Do you offer full truckload dry van service?",
    answer: `Yes. ${SITE.shortName} provides FTL dry van for dry goods and general freight from our New Jersey base across the lower 48.`,
  },
  {
    question: "What commodities can you haul in a dry van?",
    answer:
      "Packaged consumer goods, paper products, building materials, and other non-refrigerated freight. [OTHER COMMODITIES — confirm with operations if your product is specialized].",
  },
  {
    question: "Do you haul LTL or only FTL?",
    // TODO: Confirm whether Fox Trucking offers LTL or partners for partial loads.
    answer:
      "[FTL vs LTL — placeholder]. Ask dispatch when you request a quote and we’ll tell you what we can cover for your shipment size.",
  },
  {
    question: "Can you also move refrigerated freight?",
    answer:
      "Yes. When your product needs temperature control, see our refrigerated and produce freight service for reefer capacity.",
  },
  {
    question: "How do I book a dry van load?",
    answer: `Call dispatch at ${SITE.phoneDisplay} or use the Book a Load page with pickup, delivery, commodity, and weight. Real people quote — not an automated form dump.`,
  },
];

function DryVanFreightPage() {
  return (
    <InnerPage
      hero={{
        eyebrow: "Dry Van · FTL · NJ",
        title: "Dry Van & General Freight",
        intro:
          "Dry goods and general freight that need clean equipment, firm appointments, and a dispatcher who picks up — coast to coast from New Jersey, available 24/7.",
        primaryCta: { label: "Book a Load", href: "/contact" },
        secondaryCta: { label: "Call Dispatch", href: `tel:${SITE.phoneTel}` },
        showPhoneCta: false,
      }}
      breadcrumbs={breadcrumbsFor("Dry Van Freight", PATH)}
      faqs={DRY_VAN_FAQS}
      faqTitle="Dry van shipping questions."
      faqSubtitle="FTL capacity, commodities, and how to book — answers for shippers."
      bottomCta={{
        title: "Ready to book dry van freight?",
        subtitle: "Share the lane and commodity — dispatch is standing by 24/7.",
        bookLabel: "Book a Load",
        bookHref: "/contact",
      }}
    >
      {/* WHAT WE HAUL */}
      <section className="bg-background py-24">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <SectionHeader
            eyebrow="Commodities"
            title="What we haul."
            subtitle="Non-refrigerated freight that fills a trailer and needs to arrive when you promised the customer."
          />
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {HAUL_CARDS.map((c) => (
              <div
                key={c.t}
                className="group relative overflow-hidden rounded-lg border border-border bg-card p-8 transition hover:border-primary"
              >
                <c.i className="h-10 w-10 text-primary" aria-hidden="true" />
                <h3 className="mt-6 font-display text-2xl text-card-foreground">{c.t}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{c.d}</p>
              </div>
            ))}
          </div>
          <p className="mt-10 max-w-3xl text-sm text-muted-foreground">
            Need temperature control instead? See our{" "}
            <Link
              to="/refrigerated-freight"
              className="font-display tracking-wider text-primary underline-offset-2 hover:underline"
            >
              refrigerated &amp; produce freight
            </Link>{" "}
            service for reefer lanes.
          </p>
        </div>
      </section>

      {/* WHY SHIPPERS USE US */}
      <section className="bg-muted py-24">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <SectionHeader
            eyebrow="Why Fox"
            title="Why shippers use us."
            subtitle="Dry van is competitive. Service is what separates a carrier you’ll book again."
          />
          <div className="mt-14 grid gap-6 md:grid-cols-2">
            {WHY_SHIPPERS.map((s) => (
              <div
                key={s.t}
                className="rounded-lg border border-border bg-card p-8 transition hover:border-primary"
              >
                <div className="grid h-12 w-12 place-items-center rounded-md bg-primary text-primary-foreground">
                  <s.i className="h-6 w-6" aria-hidden="true" />
                </div>
                <h3 className="mt-5 font-display text-xl text-card-foreground">{s.t}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FULL TRUCKLOAD DETAILS */}
      <section className="relative bg-secondary py-24 text-secondary-foreground">
        <div className="absolute inset-0 bg-noise opacity-60" />
        <div className="relative mx-auto max-w-7xl px-4 lg:px-8">
          <SectionHeader
            tone="dark"
            eyebrow="FTL capacity"
            title="Full truckload details."
            subtitle="Specs below stay as placeholders until Fox Trucking confirms trailer dimensions and service mix for public copy."
          />
          <div className="mt-14 grid gap-6 md:grid-cols-2">
            <div className="rounded-lg border border-dashed border-secondary-foreground/25 bg-secondary/50 p-8">
              <Ruler className="h-8 w-8 text-accent" aria-hidden="true" />
              <h3 className="mt-4 font-display text-xl text-accent">Trailer size &amp; capacity</h3>
              {/* TODO: Confirm dry van trailer length, cube, and weight capacity. */}
              <p className="mt-2 font-mono text-sm text-secondary-foreground/80">
                [TRAILER SIZE/CAPACITY]
              </p>
            </div>
            <div className="rounded-lg border border-dashed border-secondary-foreground/25 bg-secondary/50 p-8">
              <Scale className="h-8 w-8 text-accent" aria-hidden="true" />
              <h3 className="mt-4 font-display text-xl text-accent">FTL vs LTL</h3>
              {/* TODO: Confirm FTL-only vs any LTL / partial-load offering. */}
              <p className="mt-2 font-mono text-sm text-secondary-foreground/80">
                [FTL vs LTL - placeholder]
              </p>
            </div>
          </div>
          <p className="mt-10 max-w-2xl text-sm text-secondary-foreground/75">
            Where we run matters as much as what fits in the trailer. See our{" "}
            <Link
              to="/coverage"
              className="font-display tracking-wider text-accent underline-offset-2 hover:underline"
            >
              coverage map and service area
            </Link>{" "}
            for Northeast and coast-to-coast lanes — or pair a dry van move with{" "}
            <Link
              to="/refrigerated-freight"
              className="font-display tracking-wider text-accent underline-offset-2 hover:underline"
            >
              refrigerated freight
            </Link>{" "}
            when part of your network needs the cold chain.
          </p>
          <div className="mt-8 inline-flex items-start gap-3 rounded-lg border border-accent/40 bg-secondary/60 p-5">
            <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-accent" aria-hidden="true" />
            <p className="text-sm text-secondary-foreground/85">
              New Jersey based · Coast-to-coast dry van · Dispatch{" "}
              <a href={`tel:${SITE.phoneTel}`} className="text-accent hover:underline">
                {SITE.phoneDisplay}
              </a>
            </p>
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="bg-background py-24">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <SectionHeader
            eyebrow="Booking flow"
            title="How a dry van load moves."
            subtitle="Four steps from brief to delivery — worded for general freight, not reefer."
          />
          <ol className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {PROCESS_STEPS.map((step) => (
              <li
                key={step.n}
                className="relative rounded-lg border border-border bg-card p-6 transition hover:border-primary"
              >
                <div className="flex items-center justify-between">
                  <step.i className="h-8 w-8 text-primary" aria-hidden="true" />
                  <span className="font-display text-2xl text-accent">{step.n}</span>
                </div>
                <h3 className="mt-5 font-display text-xl text-card-foreground">{step.t}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{step.d}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>
    </InnerPage>
  );
}
